import type { DashboardSaveV2Request, VehicleOnDeal } from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../../components/ui/form";
import { LabeledValue } from "../../../../../components/ui/labeledValue";
import { SubsectionHeader } from "../../../../../components/ui/subsectionHeader";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { useRegisterSaveableSection } from "../../../useRegisterSaveableSection";
import {
  contractVehicleSchema,
  LIC_PLATE_MAX_LENGTH,
  MOCK_CONTRACT_VEHICLE_DEFAULTS,
  ODOMETER_DISC_MILEAGE_MAX_LENGTH,
  TOTAL_SELLING_PRICE_MAX_LENGTH,
} from "./ContractVehicleInformation.schema";
import { CheckboxField, CurrencyField, DateField, TextField } from "./Fields";

interface ContractVehicleInformationProperties {
  readonly vehicleOnDeal?: VehicleOnDeal;
  /** Whether Odometer Disc Mileage, Lic Plate #, and Total Selling Price are typeable — condition TBD; defaults to disabled (today's behavior). */
  readonly editable?: boolean;
}

/**
 * Odometer Disc Mileage, Book Wholesale, Book Retail, Lic Plate #, Total
 * Selling Price, Certified Pre-Owned, and CPO Date have no source in the
 * dashboard response yet. Book Wholesale/Book Retail render a hardcoded "NA"
 * placeholder (`LabeledValue`); the rest are a spike proving out RHF + Zod on top of
 * `Fields.tsx`'s dual-mode fields — seeded from mocked defaults rather than
 * real data, since there's nothing to hydrate from yet. Certified Pre-Owned
 * is never user-editable — it (and CPO Date's visibility) just reflects
 * whatever mocked/real condition ends up driving it; CPO Date is required
 * only while visible (see `contractVehicleSchema`'s `superRefine`).
 *
 * Registered with the shared save orchestrator exactly like Signers, via
 * `useRegisterSaveableSection` — the footer Save button picks this up
 * automatically (no other wiring needed, see useSaveAllSections). Note this
 * is plumbing only: `DashboardSaveV2Request.vehicle` is documented "Not
 * writable today — accepted for round-trip convenience and ignored," so this
 * data reaches the PUT request body but the BFF discards it server-side.
 */
export function ContractVehicleInformation({ vehicleOnDeal, editable = false }: ContractVehicleInformationProperties) {
  const { t } = useTranslation();
  const form = useForm({
    resolver: zodResolver(contractVehicleSchema),
    mode: "onBlur",
    defaultValues: MOCK_CONTRACT_VEHICLE_DEFAULTS,
  });
  const { control } = form;
  const odometerDiscMileage = useController({ control, name: "odometerDiscMileage" });
  const licPlate = useController({ control, name: "licPlate" });
  const totalSellingPrice = useController({ control, name: "totalSellingPrice" });
  const isCertifiedPreOwned = useController({ control, name: "isCertifiedPreOwned" });
  const cpoDate = useController({ control, name: "cpoDate" });

  // TODO: wire to the real odometer disclosure link/modal once the destination is decided.
  const handleOdometerDiscMileageLabelClick = useCallback(() => {}, []);

  // CPO Date validates on change rather than blur (unlike the other fields
  // here) — its future-date check needs to surface as soon as a value lands,
  // not after an extra blur step. `trigger` re-runs the whole schema's
  // superRefine but only surfaces the result for this field.
  function handleCpoDateChange(value: string) {
    cpoDate.field.onChange(value);
    void form.trigger("cpoDate");
  }

  // Same as Signers' own save-time (as opposed to live-validation) error —
  // only set when the shared save call itself fails.
  const [saveError, setSaveError] = useState<string>();

  useRegisterSaveableSection("contractVehicle", {
    validate: () => form.trigger(),
    getPayload: (): Partial<DashboardSaveV2Request> => ({ vehicle: form.getValues() }),
    onSaved: () => {
      setSaveError(undefined);
      // Re-baselines RHF's dirty tracking to the just-"saved" values, same as
      // Signers' onSaved — see Signers.tsx's onSaved for why (keepValues
      // leaves the displayed values alone; only isDirty/dirtyFields clear).
      form.reset(undefined, { keepValues: true, keepDirty: false, keepErrors: true });
    },
    onSaveFailed: (error) => {
      setSaveError(getErrorMessage(error));
    },
  });

  return (
    <div>
      <SubsectionHeader>{t("vehicle.contractVehicle.heading")}</SubsectionHeader>
      {saveError && (
        <p role="alert" className="text-destructive text-2xs px-3 pt-2">
          {saveError}
        </p>
      )}
      <Form {...form}>
        <form onSubmit={(event) => event.preventDefault()} noValidate>
          <div className="gap-(--spacing-3) px-(--spacing-3) py-(--spacing-3) flex flex-col">
            <div className="gap-(--spacing-4) grid grid-cols-2 sm:grid-cols-3">
              <LabeledValue label={t("vehicle.contractVehicle.year")} value={vehicleOnDeal?.year} />
              <LabeledValue label={t("vehicle.contractVehicle.make")} value={vehicleOnDeal?.make} />
              <LabeledValue label={t("vehicle.contractVehicle.model")} value={vehicleOnDeal?.model} />
              <LabeledValue
                label={t("vehicle.contractVehicle.vin")}
                value={vehicleOnDeal?.vehicleIdentificationNumber}
              />
              <LabeledValue label={t("vehicle.contractVehicle.color")} value={vehicleOnDeal?.color} />
            </div>

            <div className="gap-(--spacing-4) mt-(--spacing-3) grid grid-cols-2 sm:grid-cols-3">
              {/* @TODO: Replace 'NA' with actual values once available from the backend */}
              {/* @TODO: Replace subLabel with actual value once available from the backend */}
              <LabeledValue
                label={t("vehicle.contractVehicle.bookWholesale")}
                value={"NA"}
                subLabel={"kelly blue book"}
              />
              <LabeledValue label={t("vehicle.contractVehicle.bookRetail")} value={"NA"} subLabel={"kelly blue book"} />
            </div>

            <div className="gap-(--spacing-4) grid grid-cols-2">
              <TextField
                label={t("vehicle.contractVehicle.odometerDiscMileage")}
                value={odometerDiscMileage.field.value}
                onChange={odometerDiscMileage.field.onChange}
                onBlur={odometerDiscMileage.field.onBlur}
                error={odometerDiscMileage.fieldState.error}
                disabled={!editable}
                maxLength={ODOMETER_DISC_MILEAGE_MAX_LENGTH}
                onLabelClick={handleOdometerDiscMileageLabelClick}
              />

              <TextField
                label={t("vehicle.contractVehicle.licPlate")}
                value={licPlate.field.value}
                onChange={licPlate.field.onChange}
                onBlur={licPlate.field.onBlur}
                error={licPlate.fieldState.error}
                disabled={!editable}
                maxLength={LIC_PLATE_MAX_LENGTH}
                uppercase
              />
              <CurrencyField
                label={t("vehicle.contractVehicle.totalSellingPrice")}
                value={totalSellingPrice.field.value}
                onChange={totalSellingPrice.field.onChange}
                onBlur={totalSellingPrice.field.onBlur}
                error={totalSellingPrice.fieldState.error}
                disabled={!editable}
                maxLength={TOTAL_SELLING_PRICE_MAX_LENGTH}
              />
              {isCertifiedPreOwned.field.value && (
                <div className="gap-(--spacing-2) flex flex-col">
                  <DateField
                    label={t("vehicle.contractVehicle.cpoDate")}
                    value={cpoDate.field.value}
                    onChange={handleCpoDateChange}
                    onBlur={cpoDate.field.onBlur}
                    error={cpoDate.fieldState.error}
                    disabled={!editable}
                  />
                  <CheckboxField
                    label={t("vehicle.contractVehicle.certifiedPreOwned")}
                    checked={isCertifiedPreOwned.field.value}
                    disabled
                  />
                </div>
              )}
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
